import { useEffect, useState } from 'react';
import { onAuthChanged, AuthChangeDetail } from '../auth';

interface ToastMessage {
  kind: 'info' | 'warn';
  text: string;
}

const DEFAULT_AUTO_DISMISS_MS = 5_000;

interface Props {
  autoDismissMs?: number;
}

// Toast surfaces a single, transient notification. Currently driven
// only by auth-change events with reason=session-expired — user-
// triggered logouts are silent, and login events don't need a toast
// (LoginForm → authed nav is a clear visual signal).
export function Toast({ autoDismissMs = DEFAULT_AUTO_DISMISS_MS }: Props = {}) {
  const [msg, setMsg] = useState<ToastMessage | null>(null);

  useEffect(() => {
    return onAuthChanged((evt: CustomEvent<AuthChangeDetail>) => {
      const reason = evt.detail?.reason;
      if (reason === 'session-expired') {
        setMsg({ kind: 'warn', text: 'Signed out — session expired.' });
      }
      // user-logout, login, external → no toast.
    });
  }, []);

  useEffect(() => {
    if (msg === null) return;
    const timer = setTimeout(() => setMsg(null), autoDismissMs);
    return () => clearTimeout(timer);
  }, [msg, autoDismissMs]);

  if (msg === null) return null;
  return (
    <div className={`toast toast-${msg.kind}`} role="status" aria-live="polite">
      <span className="toast-text">{msg.text}</span>
      <button
        type="button"
        className="toast-dismiss"
        aria-label="Dismiss notification"
        onClick={() => setMsg(null)}
      >
        ×
      </button>
    </div>
  );
}
